/**
 * Transform / array / space / clipboard order commands.
 * (waves B2/K: trf.*, arr.path/edit, order.copyBase/paste*, space.*)
 */

import * as THREE from 'three';
import {
    runInteraction, PT, PK, PN, PLoopK, requireSelection, entityToPolyline, curveEntity, dataArr, makeEntity, solidEntity,
    kernelSurfaceOf, anyMeshOf, refreshKernelTessellation,
} from './lib';
import { invertSurface, surfaceNormal } from '../../geometry/nurbs/surface';
import { undoableAdd, undoableModify } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useClipboardStore, useBlockStore } from '../../../state/commandAuxStores';
import { captureSceneClipboard, pasteSceneClipboard } from '../../scene/editRouter';
import { similarity2, resamplePolyline, centroid } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';

function selectedIdsOf(list: Entity[]): string[] { return list.map((e) => e.id); }

function translateSelected(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): string {
    const ids = useAppStore.getState().selectedIds;
    if (ids.length === 0) return 'Nothing selected to move.';
    const d: [number, number, number] = [b.x - a.x, b.y - a.y, b.z - a.z];
    undoableModify(ids, (data) => {
        const shift = (p: Pt3): Pt3 => [p[0] + d[0], p[1] + d[1], p[2] + d[2]];
        ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as Pt3); });
        ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(shift); });
        return data;
    });
    return `Transform applied to ${ids.length} object(s).`;
}

export const transformRunners: RunnerMap = {
    'trf.orient': () => {
        runInteraction({
            steps: [
                PK('ORIENT: pick the object'),
                PT('ORIENT: source point 1'), PT('ORIENT: source point 2'),
                PT('ORIENT: destination point 1'), PT('ORIENT: destination point 2'),
            ],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'ORIENT: invalid pick.';
                const f = similarity2(
                    [pts[0].x, pts[0].y, pts[0].z], [pts[1].x, pts[1].y, pts[1].z],
                    [pts[2].x, pts[2].y, pts[2].z], [pts[3].x, pts[3].y, pts[3].z]
                );
                undoableModify([picks[0]], (data) => {
                    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = f(data[k] as Pt3); });
                    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(f); });
                    return data;
                });
                return 'Object oriented via point-pair similarity transform.';
            },
        });
        return 'ORIENT: pick the object';
    },

    'trf.orientOnSrf': () => {
        runInteraction({
            steps: [PK('ORIENTONSRF: pick the object'), PK('ORIENTONSRF: pick the target surface', ['nurbs-surface', 'nurbs-polysurf', 'solid']),
                PT('ORIENTONSRF: surface location point')],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]], surf = entities[picks[1]];
                if (!e || !surf) return 'ORIENTONSRF: invalid pick.';
                // Kernel path: invert the picked point onto the surface to get
                // the real surface frame (normal + position), then translate +
                // rotate the object onto it. Mesh solids fall back to their
                // centroid + straight up normal (honest approximation).
                const srf = kernelSurfaceOf(surf);
                const probe = [pts[0].x, pts[0].y, pts[0].z] as Pt3;
                let target: Pt3; let normal: Pt3; let frameNote = 'surface frame';
                if (srf) {
                    const inv = invertSurface(srf, probe, undefined, 14);
                    target = inv.point;
                    normal = surfaceNormal(srf, inv.u, inv.v);
                } else {
                    const mesh = anyMeshOf(surf);
                    if (!mesh || mesh.vertices.length === 0) return 'ORIENTONSRF: second pick must be a surface/solid.';
                    target = centroid(mesh.vertices as Pt3[]);
                    normal = [0, 0, 1];
                    frameNote = 'mesh centroid frame (approximate)';
                }
                const up: Pt3 = [0, 0, 1];
                const axis = cross3(up, normal);
                const axisLen = Math.hypot(axis[0], axis[1], axis[2]);
                const angle = Math.acos(Math.max(-1, Math.min(1, dot3(up, normal))));
                undoableModify([picks[0]], (data) => {
                    const c = centroidOfData(data);
                    const d: [number, number, number] = [target[0] - c[0], target[1] - c[1], target[2] - c[2]];
                    const apply = (p: Pt3): Pt3 => {
                        const q: Pt3 = [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]];
                        return axisLen > 1e-9 ? rotateAbout(q, target, [axis[0] / axisLen, axis[1] / axisLen, axis[2] / axisLen], angle) : q;
                    };
                    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = apply(data[k] as Pt3); });
                    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(apply); });
                    if (data.curve && Array.isArray((data.curve as { points?: Pt3[] }).points)) {
                        const curve = data.curve as { points: Pt3[] };
                        data.curve = { ...curve, points: curve.points.map(apply) };
                    }
                    if (data.surface && Array.isArray((data.surface as { cv?: Pt3[][] }).cv)) {
                        const surf = data.surface as { cv: Pt3[][] };
                        data.surface = { ...surf, cv: surf.cv.map((row) => row.map(apply)) };
                    }
                    return refreshKernelTessellation(data);
                });
                return `Object oriented onto the ${frameNote} (position + normal-aligned rotation).`;
            },
        });
        return 'ORIENTONSRF: pick object, surface, location';
    },

    'trf.changeLayer': (args) => {
        const list = requireSelection('CHANGELAYER');
        if (!list) return 'CHANGELAYER: nothing selected.';
        const target = args?.join(' ')?.trim() || 'Default';
        const app = useAppStore.getState();
        const layer = Object.values(app.layers).find((l) => l.name === target);
        if (!layer) return `CHANGELAYER: no layer "${target}" (create it in the Layers panel).`;
        // Undoable layer move: snapshot the previous layer string per entity
        // (layer lives OUTSIDE data, so a data-only modify snapshot misses it).
        const before = list.map((e) => ({ id: e.id, layer: e.layer }));
        const after = list.map((e) => ({ id: e.id, layer: layer.name }));
        after.forEach((a) => useAppStore.getState().updateEntity(a.id, { layer: a.layer }));
        app.pushHistory({
            type: 'cmd.layer',
            timestamp: Date.now(),
            data: { count: before.length },
            undo: () => { const s = useAppStore.getState(); before.forEach((b) => s.updateEntity(b.id, { layer: b.layer })); },
            redo: () => { const s = useAppStore.getState(); after.forEach((a) => s.updateEntity(a.id, { layer: a.layer })); },
        });
        return `${list.length} object(s) moved to layer "${target}".`;
    },

    'arr.path': () => {
        runInteraction({
            steps: [PN('ARRAYPATH: number of items <4>'), PK('ARRAYPATH: pick the path curve')],
            complete: (_p, values, picks) => {
                const { entities, selectedIds } = useAppStore.getState();
                const path = entityToPolyline(entities[picks[0]]);
                if (!path || path.length < 2) return 'ARRAYPATH: path pick invalid.';
                const count = Math.max(2, Math.round(Number(values[0]) || 4));
                const sources = selectedIds.map((id) => entities[id]).filter(Boolean) as Entity[];
                if (sources.length === 0) return 'ARRAYPATH: select objects to array first.';
                const total = pathLength(path) || 1;
                void total;
                const created: Entity[] = [];
                for (let i = 0; i < count; i++) {
                    const t = i / (count - 1 || 1);
                    const pos = pointAtT(path, t);
                    sources.forEach((src) => {
                        const sc = src.data.start as Pt3 | undefined, ec = src.data.end as Pt3 | undefined;
                        if (sc && ec) {
                            const dx = ec[0] - sc[0], dy = ec[1] - sc[1];
                            created.push(makeEntity('line', `${src.name} copy`, {
                                start: [pos[0], pos[1], pos[2]], end: [pos[0] + dx, pos[1] + dy, pos[2] + (sc[2] ?? 0)],
                                // Only propagate an explicit source color; the
                                // theme ink fallback handles the default case.
                                ...(src.data.color ? { color: src.data.color as string } : {}),
                            }));
                        } else {
                            // Distribute copies along the path relative to the
                            // source's own anchor: translate by (pathPoint -
                            // sourceCentroid), never the raw absolute point
                            // (the old double-offset bug).
                            const cp = structuredClone(src.data);
                            const anchor = centroid(entityToPolyline(src) ?? [[0, 0, 0]]);
                            translateData(cp as Record<string, unknown>, [pos[0] - anchor[0], pos[1] - anchor[1], pos[2] - (anchor[2] ?? 0)]);
                            created.push(makeEntity(src.type, `${src.name} copy`, cp, src.layer));
                        }
                    });
                }
                undoableAdd(created);
                return `Path array: ${created.length} copies placed.`;
            },
        });
        return 'ARRAYPATH: items, then path';
    },

    // 'arr.edit' lives in cadDraftCommands (full array regeneration from the
    // stored params — the old metadata-only version was removed here).

    // Clipboard (COPYBASE / PASTEBLOCK / PASTEORIG)
    'order.copyBase': () => {
        const ids = useAppStore.getState().selectedIds;
        if (ids.length === 0) return 'COPYBASE: nothing selected.';
        runInteraction({
            steps: [PT('COPYBASE: specify base point')],
            complete: (pts) => {
                // B4: capture every selected citizen WITH its owning source,
                // so paste re-creates each one inside its owner store.
                const entries = captureSceneClipboard(ids);
                useClipboardStore.getState().copyCitizens(entries, [pts[0].x, pts[0].y, pts[0].z]);
                return `Copied ${entries.length} object(s) with base point.`;
            },
        });
        return 'COPYBASE: specify base point';
    },
    'order.pasteBlock': () => pasteClipboard('block'),
    'order.pasteOrig': () => pasteClipboard('orig'),
    'space.chspace': () => {
        const list = requireSelection('CHSPACE');
        if (!list) return 'CHSPACE: nothing selected.';
        notify(i18n.t('tools.notify.chspaceModelPaperContextToggledObject'), 'info');
        return `Space context switched for ${list.length} object(s).`;
    },
    'space.ncopy': () => {
        runInteraction({
            steps: [PK('NCOPY: pick a block component inside a reference')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'NCOPY: invalid pick.';
                undoableAdd([{ ...e, id: `cad:${crypto.randomUUID()}`, name: `${e.name} nested copy` }]);
                return 'Nested copy extracted as an independent object.';
            },
        });
        return 'NCOPY: pick an object';
    },
};

function pasteClipboard(mode: 'block' | 'orig'): string {
    const store = useClipboardStore.getState();
    if (store.entries.length === 0) return 'Clipboard is empty.';
    const copies = store.pasteAt();
    if (mode === 'block') {
        // PASTEBLOCK: an anonymous block definition from the copies + one
        // insert reference — NOT a geometry merge (the old behavior).
        // CAD-only by definition: a block definition is a drafting construct,
        // so it builds from the clipboard's CAD subset (`entities`).
        const pts: Pt3[] = copies.flatMap((e) => entityToPolyline(e) ?? []);
        if (pts.length === 0 && copies.length === 0) {
            return 'PASTEBLOCK: no CAD objects on the clipboard.';
        }
        const c = centroid(pts.length ? pts : [[0, 0, 0]]);
        const local = copies.map((e) => {
            const data = structuredClone(e.data) as Record<string, any>;
            const shift = (p: number[]): number[] => [(p[0] ?? 0) - c[0], (p[1] ?? 0) - c[1], (p[2] ?? 0) - (c[2] ?? 0)];
            ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k]); });
            ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(shift); });
            return { ...e, data };
        });
        const anonName = `*PASTE${Date.now().toString(36)}`;
        useBlockStore.getState().define({
            name: anonName, entities: local, basePoint: [0, 0, 0], attributes: [],
        });
        undoableAdd([makeEntity('insert', `${anonName}-ref`, {
            blockName: anonName, position: c, scale: 1, rotation: 0, attributeValues: {},
        })]);
        return `Pasted as anonymous block "${anonName}" (${local.length} components).`;
    }
    const base = store.basePoint ?? [0, 0, 0];
    // B4 cross-workbench paste: every entry re-creates inside its owner
    // store with the same base-point offset the CAD path has always applied.
    const matrix = new THREE.Matrix4().makeTranslation(base[0], base[1], base[2]);
    const newIds = pasteSceneClipboard(store.entries, matrix, 'Paste');
    return `Pasted ${newIds.length} object(s) at original coordinates.`;
}

function translateData(data: Record<string, unknown>, d: [number, number, number]) {
    const shift = (p: Pt3): Pt3 => [p[0] + d[0], p[1] + d[1], p[2] + d[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(shift); });
}

function centroidOfData(data: Record<string, unknown>): Pt3 {
    if (Array.isArray(data.vertices)) return centroid(data.vertices as Pt3[]);
    const curve = data.curve as { points?: Pt3[] } | undefined;
    if (curve && Array.isArray(curve.points) && curve.points.length) return centroid(curve.points);
    if (Array.isArray(data.controlPoints)) return centroid(data.controlPoints as Pt3[]);
    if (Array.isArray(data.position)) return data.position as Pt3;
    if (Array.isArray(data.center)) return data.center as Pt3;
    return [0, 0, 0];
}

/** Minimal vector helpers for the OrientOnSrf frame rotation. */
function cross3(a: Pt3, b: Pt3): Pt3 {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function dot3(a: Pt3, b: Pt3): number { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function rotateAbout(p: Pt3, origin: Pt3, axis: Pt3, ang: number): Pt3 {
    const [ux, uy, uz] = axis;
    const c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
    const x = p[0] - origin[0], y = p[1] - origin[1], z = (p[2] ?? 0) - origin[2];
    return [
        origin[0] + (t * ux * ux + c) * x + (t * ux * uy - s * uz) * y + (t * ux * uz + s * uy) * z,
        origin[1] + (t * ux * uy + s * uz) * x + (t * uy * uy + c) * y + (t * uy * uz - s * ux) * z,
        origin[2] + (t * ux * uz - s * uy) * x + (t * uy * uz + s * ux) * y + (t * uz * uz + c) * z,
    ];
}
function pathLength(path: Pt3[]): number {
    let l = 0;
    for (let i = 1; i < path.length; i++) l += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
    return l || 1;
}
function pointAtT(path: Pt3[], t: number): Pt3 {
    const total = pathLength(path);
    let acc = 0;
    for (let i = 1; i < path.length; i++) {
        const seg = Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
        if (acc + seg >= total * t) {
            const k = (total * t - acc) / (seg || 1);
            return [
                path[i - 1][0] + (path[i][0] - path[i - 1][0]) * k,
                path[i - 1][1] + (path[i][1] - path[i - 1][1]) * k,
                path[i - 1][2],
            ];
        }
        acc += seg;
    }
    return path[path.length - 1];
}
